/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import CloseIcon from '@edna/icons/close.svg';
import VisibilityIcon from '@edna/icons/visibility.svg';
import VisibilityOffIcon from '@edna/icons/visibilityOff.svg';

import {TComponentRef, formField, newFormField} from '../FormField';
import {TUseMeasureRef, useMeasure} from '../utils';
import * as S from './style';

export type TValue = string | number;

type TControlProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'capture'> & {
  value?: TValue;
};

export type TProps = TComponentRef &
  TControlProps & {
    className?: string;
    resetValue?: TValue;
    isInvalid?: boolean;
    isEnableReset?: boolean;
    leftChildren?: React.ReactNode;
    rightChildren?: React.ReactNode;
    renderInput?: typeof renderTextInput;
    dataSelector?: string;
  };

export type TRenderTextInputProps = TControlProps & {
  leftChildrenWidth: number;
  rightChildrenWidth: number;
};

const renderTextInput = (inputProps: TRenderTextInputProps) => <S.Input {...inputProps} />;

type TRightChildrenProps = {
  value: TProps['value'];
  resetValue: TProps['resetValue'];
  type: TProps['type'];
  isEnableReset?: TProps['isEnableReset'];
  disabled: TProps['disabled'];
  readOnly: TProps['readOnly'];
  rightChildren: TProps['rightChildren'];
  onChange: TProps['onChange'];
  rightChildrenRef: TUseMeasureRef<HTMLDivElement>;
  currentType: string;
  setCurrentType: (type: string) => void;
};

const RightChildren = React.memo<TRightChildrenProps>(
  ({
    value,
    type,
    isEnableReset,
    onChange,
    rightChildrenRef,
    resetValue,
    currentType,
    disabled,
    readOnly,
    setCurrentType,
    rightChildren,
  }) => {
    const isRenderPasswordShow = type === 'password';
    const isPasswordShown = currentType === 'text';
    const isRenderReset = React.useMemo(
      () => isEnableReset && String(value) !== String(resetValue),
      [isEnableReset, resetValue, value],
    );

    const handleReset = React.useCallback(() => {
      if (!onChange || disabled || readOnly) {
        return;
      }
      onChange({target: {value: resetValue}} as React.ChangeEvent<HTMLInputElement>);
    }, [disabled, onChange, readOnly, resetValue]);

    const handleChangeType = React.useCallback(() => {
      if (!disabled && !readOnly) {
        setCurrentType(isPasswordShown ? 'password' : 'text');
      }
    }, [disabled, isPasswordShown, readOnly, setCurrentType]);

    if (!rightChildren && !isRenderReset && !isRenderPasswordShow) {
      return null;
    }

    return (
      <S.Children right ref={rightChildrenRef} readOnly={readOnly}>
        {rightChildren}
        {isRenderPasswordShow && (
          <S.Button onClick={handleChangeType} disabled={disabled}>
            <S.Icon as={isPasswordShown ? VisibilityOffIcon : VisibilityIcon} />
          </S.Button>
        )}
        {isRenderReset && (
          <S.Button onClick={handleReset} disabled={disabled || readOnly}>
            <S.Icon as={CloseIcon} title="clear icon" />
          </S.Button>
        )}
      </S.Children>
    );
  },
);

const TextInput = React.memo<TProps>(
  ({
    id: propId,
    type = 'text',
    value,
    resetValue = '',
    leftChildren,
    rightChildren,
    isEnableReset,
    onChange,
    onFocus,
    onBlur,
    className,
    readOnly,
    disabled,
    renderInput = renderTextInput,
    innerRef,
    dataSelector,
    ...props
  }) => {
    const [currentType, setCurrentType] = React.useState(type);
    const [leftChildrenRef, {width: leftChildrenWidth}] = useMeasure<HTMLDivElement>();
    const [rightChildrenRef, {width: rightChildrenWidth}] = useMeasure<HTMLDivElement>();

    const inputProps = {
      ...props,
      ref: innerRef,
      readOnly,
      disabled,
      onChange: disabled || readOnly ? undefined : onChange,
      onFocus: disabled ? undefined : onFocus,
      onBlur: disabled ? undefined : onBlur,
      value,
      type: currentType,
      id: propId ?? props.name,
      leftChildrenWidth,
      rightChildrenWidth,
    };

    return (
      <S.TextInput
        className={className}
        title={readOnly && typeof value === 'string' ? value : ''}
        data-selector={dataSelector}
      >
        {renderInput(inputProps)}
        {leftChildren && (
          <S.Children left ref={leftChildrenRef}>
            {leftChildren}
          </S.Children>
        )}
        <RightChildren
          rightChildren={rightChildren}
          rightChildrenRef={rightChildrenRef}
          type={type}
          currentType={currentType}
          setCurrentType={setCurrentType}
          onChange={onChange}
          resetValue={resetValue}
          value={value}
          isEnableReset={isEnableReset}
          disabled={disabled}
          readOnly={readOnly}
        />
      </S.TextInput>
    );
  },
);

export default TextInput;

export const TextInputField = formField<TProps, TValue>(TextInput);

export const NewTextInputField = newFormField<TProps>(TextInput);
/* eslint-enable react/jsx-props-no-spreading */
